import React from 'react';
import {
  List,
  Show,
  useListContext,
  useShowContext,
  useRefresh,
  useDataProvider,
} from 'react-admin';
import { RefreshCw } from 'lucide-react';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { StatusBadge } from '../components/ui/data-cells';
import { toastSuccess, toastError } from '../lib/toast';
import { CustomDataProvider } from '../types/dataProvider';

interface WebhookItem {
  id: number;
  provider: string;
  event_type: string;
  event_id?: string;
  processed: boolean;
  payment_id?: number;
  error_message?: string;
  processed_at?: string;
  created_at: string;
}
interface WebhookDetail extends WebhookItem {
  payload?: any;
  headers?: any;
}
const fmtDate = (v?: string | null) =>
  v
    ? new Date(v).toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'false', label: 'Unprocessed' },
  { value: 'true', label: 'Processed' },
];
const Tabs = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = filterValues.processed === undefined ? 'all' : String(filterValues.processed);
  return (
    <SegmentedTabs
      tabs={TABS}
      value={current}
      onChange={(v) =>
        setFilters({ ...filterValues, processed: v === 'all' ? undefined : v === 'true' }, displayedFilters)
      }
    />
  );
};

const ListBody = () => {
  const columns: DataColumn<WebhookItem>[] = [
    { key: 'id', header: 'ID', width: '1px', cell: (r) => <span className="tabular-nums text-ink-2">#{r.id}</span> },
    { key: 'event_type', header: 'Event', cell: (r) => <span className="text-ink">{r.event_type}</span> },
    { key: 'payment_id', header: 'Payment', cell: (r) => (r.payment_id ? <span className="tabular-nums text-ink">#{r.payment_id}</span> : <span className="text-ink-3">—</span>) },
    { key: 'created_at', header: 'Received', cell: (r) => fmtDate(r.created_at) },
    { key: 'error', header: 'Error', cell: (r) => (r.error_message ? <span className="truncate text-[12px] text-error">{r.error_message}</span> : <span className="text-ink-3">—</span>) },
    { key: 'processed', header: 'State', cell: (r) => <StatusBadge status={r.processed ? 'processed' : 'pending'} /> },
  ];
  return (
    <ListScreen tabs={<Tabs />}>
      <DataTable columns={columns} rowClick="show" emptyMessage="No webhook events." />
      <div className="pt-1">
        <ReactAdminPagination />
      </div>
    </ListScreen>
  );
};

export const WebhookList = (props: any) => (
  <List {...props} actions={false} empty={false} pagination={false} sort={{ field: 'created_at', order: 'DESC' }} perPage={25} component="div">
    <ListBody />
  </List>
);

const ShowContent = () => {
  const { record, isLoading } = useShowContext<WebhookDetail>();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const [busy, setBusy] = React.useState(false);
  if (isLoading) return <div className="p-6 text-center text-ink-2">Loading…</div>;
  if (!record) return <div className="p-6 text-center text-ink-2">Webhook not found</div>;
  const reprocess = async () => {
    setBusy(true);
    try {
      await dataProvider.reprocessWebhook({ id: record.id });
      toastSuccess('Webhook reprocessed');
      refresh();
    } catch (e: any) {
      toastError(e?.message || 'Reprocess failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>Webhook #{record.id}</h1>
            <StatusBadge status={record.processed ? 'processed' : 'pending'} />
          </div>
          <p className="mt-1 text-sm text-ink-2">
            {record.event_type}
            {record.payment_id && ` · payment #${record.payment_id}`}
          </p>
        </div>
        <button onClick={reprocess} disabled={busy} className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2 disabled:opacity-50">
          <RefreshCw className="size-4" />
          {busy ? 'Reprocessing…' : 'Reprocess'}
        </button>
      </div>
      {record.error_message && (
        <div className="rounded-card border border-error/30 bg-error-bg px-4 py-3 text-sm text-error">
          <strong>Error:</strong> {record.error_message}
        </div>
      )}
      <div className="rounded-card border border-hairline bg-background p-5">
        <h3 className="mb-3 text-[15px] font-bold text-ink">Payload</h3>
        <pre className="overflow-auto rounded-lg bg-surface-2 p-4 text-[12px] text-ink">{JSON.stringify(record.payload, null, 2)}</pre>
      </div>
      {record.headers && (
        <div className="rounded-card border border-hairline bg-background p-5">
          <h3 className="mb-3 text-[15px] font-bold text-ink">Headers</h3>
          <pre className="overflow-auto rounded-lg bg-surface-2 p-4 text-[12px] text-ink">{JSON.stringify(record.headers, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export const WebhookShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <ShowContent />
  </Show>
);
